import { Link } from "react-router-dom";
import {
  Phone,
  ArrowRight,
  Headset,
  MessageSquare,
  CheckCircle,
  Shield,
  Globe,
  Eye,
  Target,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import Layout from "@/components/layout/Layout";
import Seo from "@/components/Seo";
import { HERO_BG, TEAM_MEMBERS, SERVICES, CONTACT_INFO } from "@/data/siteData";

const HomePage = () => {
  return (
    <Layout showContactStrip={false}>
      <Seo path="/" />
      {/* Hero Section */}
      <section
        data-testid="hero-section"
        className="hero-section"
        style={{ backgroundImage: `url(${HERO_BG})` }}
      >
        <div className="absolute inset-0 hero-overlay" />
        <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-8 py-32">
          <div className="max-w-4xl animate-fade-in-up">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
              Аутсорсинг Кол-център специализиран в обслужването на крайни клиенти
            </h1>
            <p className="text-lg md:text-xl text-slate-200 mb-8 leading-relaxed max-w-2xl">
              Цялостно обслужване на изходяща и входяща телефонна линия в работата
              с крайни клиенти във всички сфери на бизнеса по доказани аутсорсинг
              практики.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/zapazi-chas">
                <Button
                  className="bg-[#2ED573] text-slate-900 hover:bg-[#25b861] text-lg px-8 h-14 rounded-lg font-semibold transition-all"
                  data-testid="hero-cta-appointment"
                >
                  Изпратете Запитване
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <a href={`tel:${CONTACT_INFO.phone}`}>
                <Button
                  variant="outline"
                  className="bg-white text-[#2E4A91] hover:bg-slate-100 border-white text-lg px-8 h-14 rounded-lg font-semibold transition-all"
                  data-testid="hero-cta-call"
                >
                  <Phone className="mr-2 w-5 h-5" />
                  Обадете се
                </Button>
              </a>
            </div>
          </div>
        </div>

        {/* Scroll indicator */}
        <button
          onClick={() =>
            document
              .getElementById("intro")
              ?.scrollIntoView({ behavior: "smooth" })
          }
          className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce cursor-pointer"
          aria-label="Скрол надолу"
        >
          <div className="w-8 h-12 rounded-full border-2 border-white/50 flex items-start justify-center p-2 hover:border-white transition-colors">
            <div className="w-1 h-3 bg-white/70 rounded-full animate-pulse" />
          </div>
        </button>
      </section>

      {/* Brief Introduction */}
      <section id="intro" className="section-padding bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-12">
            <span className="text-sm font-semibold text-[#2ED573] uppercase tracking-wider">
              За компанията
            </span>
            <h2 className="section-title text-3xl md:text-4xl lg:text-5xl mt-3 section-title-center">
              Повече от бизнес
            </h2>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <p className="text-lg text-slate-600 leading-relaxed">
                <span className="font-semibold text-[#2E4A91]">
                  „Аутсорс Пойнт Груп" ЕООД
                </span>{" "}
                е българска аутсорсинг компания, специализирана в предоставянето
                на кол център услуги и други телекомуникационни решения за
                взаимодействие с крайни клиенти.
              </p>
              <p className="text-lg text-slate-600 leading-relaxed">
                Основана през 2016 година, компанията непрекъснато
                усъвършенства своя екип и услуги, специализирайки се в изходящи
                и входящи кампании за обслужване на клиенти.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 pt-4">
                <Link to="/za-nas">
                  <Button className="btn-primary">
                    Научете повече за нас
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </Link>
                <Link to="/kontakti">
                  <Button variant="outline" className="btn-outline">
                    Свържете се с нас
                  </Button>
                </Link>
              </div>
            </div>

            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1551434678-e076c223a692?auto=format&fit=crop&w=800&q=80"
                alt="Екип на Outsource Point"
                className="rounded-2xl shadow-xl"
              />
              <div className="absolute -bottom-6 -left-6 bg-[#2E4A91] text-white p-6 rounded-xl shadow-lg">
                <div className="text-4xl font-bold">2016</div>
                <div className="text-sm opacity-80">Година на основаване</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Preview */}
      <section className="section-padding bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-12">
            <span className="text-sm font-semibold text-[#2ED573] uppercase tracking-wider">
              Какво предлагаме
            </span>
            <h2 className="section-title text-3xl md:text-4xl lg:text-5xl mt-3 section-title-center">
              Нашите услуги
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <div className="bg-white p-8 rounded-2xl shadow-sm border border-slate-100 card-hover">
              <div className="service-card-icon mb-6">
                <Headset className="w-6 h-6" />
              </div>
              <h3 className="text-xl font-bold text-[#2E4A91] mb-3">
                Кол Център
              </h3>
              <p className="text-slate-600 mb-4">
                Входящи и изходящи линии, денонощна поддръжка, търговски кампании
                и проучвания.
              </p>
              <Link
                to="/uslugi"
                className="text-[#2E4A91] font-medium flex items-center gap-2 hover:text-[#2ED573] transition-colors"
              >
                Научете повече <ArrowRight className="w-4 h-4" />
              </Link>
            </div>

            <div className="bg-white p-8 rounded-2xl shadow-sm border border-slate-100 card-hover">
              <div
                className="service-card-icon mb-6"
                style={{
                  background: "linear-gradient(135deg, #2ED573 0%, #25b861 100%)",
                }}
              >
                <MessageSquare className="w-6 h-6" />
              </div>
              <h3 className="text-xl font-bold text-[#2E4A91] mb-3">
                Чат Канали
              </h3>
              <p className="text-slate-600 mb-4">
                Управление на WhatsApp, Viber, Messenger, Instagram и други
                платформи.
              </p>
              <Link
                to="/uslugi"
                className="text-[#2E4A91] font-medium flex items-center gap-2 hover:text-[#2ED573] transition-colors"
              >
                Научете повече <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>

          <div className="text-center mt-12">
            <Link to="/uslugi">
              <Button className="btn-primary text-lg px-8 py-6">
                Вижте всички услуги
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="section-padding bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-12">
            <span className="text-sm font-semibold text-[#2ED573] uppercase tracking-wider">
              Защо да ни изберете
            </span>
            <h2 className="section-title text-3xl md:text-4xl lg:text-5xl mt-3 section-title-center">
              Нашите принципи
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center p-8 bg-slate-50 rounded-2xl card-hover">
              <div className="w-16 h-16 mx-auto mb-6 bg-[#2E4A91]/10 rounded-full flex items-center justify-center">
                <Eye className="w-8 h-8 text-[#2E4A91]" />
              </div>
              <h4 className="text-xl font-bold mb-3 text-[#2E4A91]">
                Прозрачност
              </h4>
              <p className="text-slate-600">
                Работим прозрачно. Партньорите имат достъп до системата за
                наблюдение на агенти и кампании.
              </p>
            </div>
            <div className="text-center p-8 bg-slate-50 rounded-2xl card-hover">
              <div className="w-16 h-16 mx-auto mb-6 bg-[#2ED573]/10 rounded-full flex items-center justify-center">
                <Shield className="w-8 h-8 text-[#2ED573]" />
              </div>
              <h4 className="text-xl font-bold mb-3 text-[#2E4A91]">
                Коректност
              </h4>
              <p className="text-slate-600">
                Поддържаме коректни отношения. Без скрити такси или комисионни.
                Фокус върху дългосрочни партньорства.
              </p>
            </div>
            <div className="text-center p-8 bg-slate-50 rounded-2xl card-hover">
              <div className="w-16 h-16 mx-auto mb-6 bg-[#2E4A91]/10 rounded-full flex items-center justify-center">
                <Target className="w-8 h-8 text-[#2E4A91]" />
              </div>
              <h4 className="text-xl font-bold mb-3 text-[#2E4A91]">
                Отговорност
              </h4>
              <p className="text-slate-600">
                Подхождаме отговорно към всеки проблем или задача. Стремим се да
                постигнем целите заедно.
              </p>
            </div>
          </div>

          <div className="text-center mt-12">
            <Link to="/za-nas">
              <Button variant="outline" className="btn-outline text-lg px-8 py-6">
                Научете повече за нас
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Team Preview */}
      <section className="section-padding bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-12">
            <span className="text-sm font-semibold text-[#2ED573] uppercase tracking-wider">
              Нашият екип
            </span>
            <h2 className="section-title text-3xl md:text-4xl lg:text-5xl mt-3 section-title-center">
              Професионалисти зад успеха
            </h2>
          </div>

          <div className="flex flex-wrap justify-center gap-8 md:gap-12">
            {TEAM_MEMBERS.map((member, index) => (
              <div key={index} className="text-center w-44">
                <img
                  src={member.image}
                  alt={member.name}
                  className="w-32 h-32 mx-auto rounded-full object-cover object-top shadow-lg mb-4"
                />
                <h4 className="font-semibold text-[#2E4A91]">{member.name}</h4>
                <p className="text-sm text-slate-500">{member.position}</p>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link to="/ekip">
              <Button className="btn-primary">
                Запознайте се с екипа
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Certifications */}
      <section className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <h3 className="text-center text-lg font-semibold text-slate-400 mb-8">
            Сертификати и доверие
          </h3>
          <div className="flex flex-wrap justify-center items-center gap-8 md:gap-12">
            <div className="flex flex-col items-center gap-2 opacity-60 hover:opacity-100 transition-opacity">
              <Shield className="w-12 h-12 text-slate-400" />
              <span className="text-xs text-slate-500">ISO 9001</span>
            </div>
            <div className="flex flex-col items-center gap-2 opacity-60 hover:opacity-100 transition-opacity">
              <Shield className="w-12 h-12 text-slate-400" />
              <span className="text-xs text-slate-500">ISO 27001</span>
            </div>
            <div className="flex flex-col items-center gap-2 opacity-60 hover:opacity-100 transition-opacity">
              <Shield className="w-12 h-12 text-slate-400" />
              <span className="text-xs text-slate-500">PCI DSS</span>
            </div>
            <div className="flex flex-col items-center gap-2 opacity-60 hover:opacity-100 transition-opacity">
              <Globe className="w-12 h-12 text-slate-400" />
              <span className="text-xs text-slate-500">GDPR</span>
            </div>
            <div className="flex flex-col items-center gap-2 opacity-60 hover:opacity-100 transition-opacity">
              <CheckCircle className="w-12 h-12 text-slate-400" />
              <span className="text-xs text-slate-500">AAA Рейтинг</span>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-gradient-to-r from-[#2E4A91] to-[#243b75] py-16">
        <div className="max-w-7xl mx-auto px-4 md:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Готови ли сте да започнем?
          </h2>
          <p className="text-lg text-white/80 mb-8 max-w-2xl mx-auto">
            Свържете се с нас днес и нека обсъдим как можем да подобрим
            обслужването на вашите клиенти.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/zapazi-chas">
              <Button className="bg-white text-[#2E4A91] hover:bg-slate-100 text-lg px-8 h-14 rounded-lg font-semibold">
                Запазете консултация
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <Link to="/ceni">
              <Button
                variant="outline"
                className="border-white text-white hover:bg-white/10 text-lg px-8 h-14 rounded-lg font-semibold"
              >
                Вижте цените
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default HomePage;
